import type * as Payload from '@/types';

import { isNotScalar } from '../isNotScalar';

type HrefEntry =
    | Payload.CaseStudy
    | Payload.Story
    | Payload.Category
    | Payload.Example
    | Payload.ExampleCategory;

export type CommonOptions = {
    /**
     * If omitted, this option will default to `true`, unless `categorySlug`, `parentCategorySlug` or `rootCategorySlug` are provided.
     * When set to `false`, the function will return full URL where applicable.
     */
    useCanonicalUrl?: boolean;
    fallback?: string;
};

type NestedArticleOptions = {
    categorySlug: string;
    parentCategorySlug?: string;
    rootCategorySlug?: string;
};

type ParentCategoryOptions = {
    parentCategorySlug: string;
    rootCategorySlug?: string;
};

type PayloadArticleOptions = {
    category?: Payload.Category;
};

type PressReleaseExampleOptions = {
    modal?: boolean;
};

type PrezlyThemeOptions = {
    preview?: boolean;
};

export function createNextRouteHref(
    entry: Payload.Example,
    options?: CommonOptions & PressReleaseExampleOptions,
): string;

export function createNextRouteHref(
    entry: Payload.Category,
    options?: CommonOptions & ParentCategoryOptions,
): string;

export function createNextRouteHref(
    entry: Payload.Story,
    options?: CommonOptions & PayloadArticleOptions,
): string;

export function createNextRouteHref(
    entry: HrefEntry,
    options?: Partial<
        CommonOptions &
            NestedArticleOptions &
            ParentCategoryOptions &
            PressReleaseExampleOptions &
            PrezlyThemeOptions
    >,
): string;

export function createNextRouteHref(
    entry: HrefEntry,
    options: Partial<
        CommonOptions &
            NestedArticleOptions &
            ParentCategoryOptions &
            PressReleaseExampleOptions &
            PrezlyThemeOptions &
            PayloadArticleOptions
    > = {},
): string {
    const defaultFallback = options.fallback ?? '/';

    if (entry.contentType === 'casestudies') {
        return entry.path;
    }

    if (entry.contentType === 'examples') {
        const { slug } = entry;
        const { modal } = options;
        return modal ? `#${slug}` : `/press-release-examples/${slug}`;
    }

    if (entry.contentType === 'example-category') {
        const { slug } = entry;
        return `/press-release-examples/${slug}`;
    }

    if (entry.contentType === 'stories') {
        const category = options.category ?? entry.categories?.filter(isNotScalar)[0];

        if (options.useCanonicalUrl || !category) {
            return entry.path;
        }

        return `${createNextRouteHref({ contentType: 'categories', ...category })}/${entry.slug}`;
    }

    if (entry.contentType === 'categories') {
        if (entry.path) {
            return entry.path;
        }

        // It is expected that all categories have `path` precalculated,
        // but, just in case, we provide this fallback logic to render at least something.

        if (entry.slug === 'news' || entry.site === 'changelog') {
            return `/${entry.site}/category/${entry.slug}`;
        }

        return `/${entry.site}/${entry.slug}`;
    }

    console.error(`No route logic found in namespace for node: `, entry);

    return defaultFallback;
}
